import { Context } from '@nuxt/types'

import { authModule } from '@/store'
import {
  anonymousRoutes,
  authCookieName,
  getAccountTypeFromToken,
} from '@/utils/auth'

// Custom error code that the server returns when the token is expired
const tokenExpiredErrorCode = 13040000

export default function ({
  app: { $axios, router, $cookies },
  req,
  redirect,
}: Context) {
  function getInitialRoutePath(): string {
    if (process.server && req?.url) {
      const url = new URL(req.url, `http://${req.headers.host || 'localhost'}`)
      return url.pathname
    } else if (process.client && typeof window !== 'undefined') {
      return window.location.pathname
    }
    return '/'
  }

  let currentRoutePath = getInitialRoutePath()
  router?.afterEach((to) => {
    currentRoutePath = to.path
  })

  function signOut() {
    $cookies.remove(authCookieName)
    if (process.client) {
      localStorage.clear()
    }

    redirect('/sign-in')
  }

  $axios.onError(async (error) => {
    if ($axios.isCancel(error)) {
      throw error
    }

    const statusCode = error.response ? error.response.status : -1
    const lambusErrorCode = error.response ? error.response.data.code : -1
    const config = error.config

    if (config.url?.includes('api/authenticate')) {
      // Do not attempt any automatic recovery for authenticate routes
      throw error
    }

    if (config.isRetry || authModule.isRefreshing) {
      // We are already in a retry or the token is being refreshed, there is no way
      // to recover from this
      signOut()
      throw error
    }

    const isTokenExpired =
      statusCode === 401 && lambusErrorCode === tokenExpiredErrorCode

    if (
      isTokenExpired &&
      !authModule.isRefreshing &&
      authModule.refreshToken &&
      !config.isRetry
    ) {
      try {
        await authModule.refresh()
        config.isRetry = true
        config.headers.Authorization = `Bearer ${authModule.token}`
        return $axios(config)
      } catch (e) {
        signOut()
        throw error
      }
    }

    const oldTokenAccountType = getAccountTypeFromToken(authModule.token)
    if (
      statusCode === 401 &&
      oldTokenAccountType === 'anonymous' &&
      anonymousRoutes.some((path) => currentRoutePath.startsWith(path))
    ) {
      // Redirect anonymous users back to the AI onboarding to automatically
      // start a new onboarding session.
      $axios.setToken(false, 'Bearer')
      const { data } = await $axios.post('/api/users/create-anonymous')

      $axios.setToken(data.token, 'Bearer')
      authModule.setTokens({
        accessToken: data.token,
      })
      authModule.setUser(data.user)
      config.isRetry = true
      config.headers.Authorization = `Bearer ${data.token}`
      return $axios(config)
    }

    if (statusCode === 401) {
      // For any other 401 Unauthorized errors we just clear the local auth state and
      // redirect to the sign in page
      signOut()
      throw error
    }

    throw error
  })
}
